import { trackAddNewUserInChat } from './tracking';
import { IMessageEvent, IWsFileMessage, IWsMessageEvent, IWsMessageReply } from './types';
import { BLOB_ID } from '../../constants/blobId';
import { GuardError } from '../../errors/GuardError';
import { IOffer } from '../../repositories/chats/entities/get_my_offers_for_selection/Offer';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TMessageSchema } from '../../types/messages';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getTelemetryKeyByPlatformAndHostType } from '../../utils/getTelemetryKeyByPlatformAndHostType';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { setSendMessageFailed } from '../sendMessage';
import { ensureEventChannelOpen } from '../socket/ensureEventChannelOpen';

/**
 * Отправка сообщения по WS
 */

interface IProps {
  message: TMessageSchema;
  files?: IWsFileMessage[];
  offers?: IOffer[];
  reply?: IWsMessageReply | null;
}

const DEFAULT_MESSAGE_SEND_TIMEOUT = 10000;

export const sendWsMessage =
  ({ message, files, offers, reply }: IProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, config, custom: { messagesMap, socket, realDevice, hostType } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const state = getState();

    const opponentId = currentChatOpponentIdSelector(state);
    const { users, offer } = currentOpenChatSelector(state) || {};

    const { chatId, messageId, text, userId } = message;

    const isMessageUserInChat = users?.find(user => user.userId === userId);

    const messageEvent: IMessageEvent = {
      chatId,
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Message,
      timestamp: Date.now(),
    };

    try {
      const initTelemetryKey = getTelemetryKeyByPlatformAndHostType({
        eventKey: ETelemetryEvents.ChatWsMessageSendInit,
        deviceType: realDevice,
        hostType,
      });

      telemetry.increment(initTelemetryKey);

      const isConnected = await dispatch(ensureEventChannelOpen());

      if (!isConnected) {
        dispatch(setSocketStatusFailed());

        throw new GuardError({
          message: 'Соединение закрыто',
          domain: EExceptionDomain.MessageSend,
        });
      }

      if (!chatId || chatId === BLOB_ID) {
        throw new GuardError({
          message: 'Чат не выбран',
          domain: EExceptionDomain.MessageSend,
        });
      }

      clientEvents.set(messageId, messageEvent);

      const wsMessageEvent: IWsMessageEvent = {
        type: EWsClientEventType.Message,
        message: {
          chatId,
          text,
          files,
          offers,
          reply,
        },
        uid: messageId,
        userId,
      };

      const messageSendTimeout =
        config.get<number>('frontend-chats.messageSendTimeout') || DEFAULT_MESSAGE_SEND_TIMEOUT;

      await timeoutPromise(eventChannel.sendMessage(wsMessageEvent), messageSendTimeout, EExceptionDomain.MessageSend);

      if (!isMessageUserInChat) {
        trackAddNewUserInChat({ offer, chatId, opponentId });
      }
    } catch (error) {
      telemetry.increment(ETelemetryEvents.ChatWsMessageSendFail);

      const errorToLog = getGenericLikeError({
        error,
        domain: EExceptionDomain.MessageSend,
        message: 'Ошибка при отправке сообщения по ws',
      });

      logger.error(errorToLog);

      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId, messagesMap }));
    }
  };
